import { supabase } from "@/integrations/supabase/client";
import { Invoice } from "@/types/payments";
import type { CategoryMetadata } from "@/types/expense";
import { failureFromResponse, userFacingErrorMessage } from "@/utils/userFacingError";
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

/**
 * Client for interacting with invoice data via edge functions
 */
export class InvoiceApiClient {
  private static readonly EDGE_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invoice-api`;

  /**
   * Helper to make authenticated requests to the edge function
   */
  private static async makeRequest(action: string, params: Record<string, any> = {}): Promise<any> {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(this.EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ action, ...params }),
    });

    if (!response.ok) {
      throw await failureFromResponse(response, 'Request failed');
    }

    return response.json();
  }

  /**
   * Get all invoices
   * @param onlyApproved Whether to return only approved invoices
   * @returns Array of invoices
   */
  public static async getInvoices(onlyApproved: boolean = false): Promise<{ invoices: Invoice[] }> {
    try {
      console.log("InvoiceApiClient: Fetching all invoices", onlyApproved ? "(approved only)" : "");
      const result = await this.makeRequest('getInvoices', { onlyApproved });
      console.log("InvoiceApiClient: Received invoices response:", { invoices: result.invoices.length });
      return result;
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
      throw error;
    }
  }

  /**
   * Get pending invoices for approval
   * @returns Array of pending invoices
   */
  static async getPendingInvoices(): Promise<{ invoices: Invoice[] }> {
    try {
      console.log("InvoiceApiClient: Fetching pending invoices");
      const result = await this.makeRequest('getPendingInvoices');
      console.log("InvoiceApiClient: Received pending invoices:", result.invoices.length);
      return result;
    } catch (error) {
      console.error("Failed to fetch pending invoices:", error);
      throw error;
    }
  }

  /**
   * Get approved invoices
   * @returns Array of approved invoices
   */
  static async getApprovedInvoices(): Promise<{ invoices: Invoice[] }> {
    try {
      console.log("InvoiceApiClient: Fetching approved invoices");
      const result = await this.makeRequest('getApprovedInvoices');
      console.log("InvoiceApiClient: Received approved invoices:", result.invoices.length);
      return result;
    } catch (error) {
      console.error("Failed to fetch approved invoices:", error);
      throw error;
    }
  }

  /**
   * Approve an invoice
   * @param invoiceId ID of the invoice to approve
   * @returns Result of the approval operation
   */
  static async approveInvoice(invoiceId: string): Promise<{ success: boolean; invoice?: Invoice }> {
    try {
      console.log("InvoiceApiClient: Approving invoice:", invoiceId);
      const result = await this.makeRequest('approveInvoice', { invoiceId });
      console.log("InvoiceApiClient: Approval result:", result.success);
      return result;
    } catch (error) {
      console.error("Failed to approve invoice:", error);
      return { success: false };
    }
  }

  /**
   * Update the status of an invoice
   * @param invoiceId ID of the invoice to update
   * @param status New status for the invoice
   * @returns Result of the update operation
   */
  static async updateInvoiceStatus(
    invoiceId: string,
    status: "pending" | "paid"
  ): Promise<{ success: boolean; invoice?: Invoice }> {
    try {
      console.log("InvoiceApiClient: Updating invoice status:", invoiceId, status);
      const result = await this.makeRequest('updateInvoiceStatus', { invoiceId, status });
      console.log("InvoiceApiClient: Status update result:", result.success);
      return result;
    } catch (error) {
      console.error("Failed to update invoice status:", error);
      return { success: false };
    }
  }

  /**
   * Get a signed URL for an invoice file with expiry
   */
  static async getInvoiceFileUrl(filePath: string): Promise<string | null> {
    try {
      console.log("InvoiceApiClient: Getting signed URL for file path:", filePath);
      const result = await this.makeRequest('getInvoiceFileUrl', { filePath });
      console.log("InvoiceApiClient: File URL result:", result.fileUrl ? 'success' : 'failed');
      return result.fileUrl;
    } catch (error) {
      console.error("Error getting file URL:", error);
      return null;
    }
  }

  /**
   * The storage path and name of a document, read under the caller's own RLS (the same read the
   * client portal's viewer does), so a consultant can open a receipt a todo only names by id.
   */
  static async getDocumentFile(documentId: string): Promise<{ filePath: string; fileName: string | null } | null> {
    const { data, error } = await supabase
      .from('financial_documents')
      .select('file_path, file_name')
      .eq('id', documentId)
      .maybeSingle();
    if (error) throw error;
    return data?.file_path ? { filePath: data.file_path, fileName: data.file_name } : null;
  }

  /**
   * Extract invoice data from PDF
   */
  static async extractInvoiceData(
    filePath: string,
    documentType: 'payable' | 'receivable' | 'receipt' | 'expense'
  ): Promise<{ data: Record<string, any>; metadata: any }> {
    try {
      console.log("InvoiceApiClient: Extracting data from document:", filePath);
      const result = await this.makeRequest('extractInvoiceData', { filePath, documentType });
      console.log("InvoiceApiClient: Extraction status:", result.metadata.extractionStatus);
      return result;
    } catch (error) {
      console.error("Failed to extract invoice data:", error);
      throw error;
    }
  }

  /**
   * Create a draft invoice with extracted data
   */
  static async createDraftInvoice(params: {
    invoiceData: Record<string, any>;
    extractionMetadata: any;
    filePath: string;
    fileName: string;
    companyId: string;
    documentType: 'payable' | 'receivable' | 'receipt' | 'expense';
    vendorAccountFields?: {
      vendor_iban?: string | null;
      vendor_bankgiro?: string | null;
      vendor_plusgiro?: string | null;
      vendor_account_number?: string | null;
      vendor_clearing_number?: string | null;
    } | null;
    categoryMetadata?: CategoryMetadata;
  }): Promise<{ invoiceId: string }> {
    try {
      console.log("InvoiceApiClient: Creating draft invoice");
      console.log("InvoiceApiClient: Vendor account fields:", params.vendorAccountFields);
      // Issue #82: Backend reads category_metadata from invoiceData (snake_case).
      // Merge it in here so the wire payload matches the server contract.
      const { categoryMetadata, invoiceData, ...rest } = params;
      const mergedInvoiceData =
        categoryMetadata !== undefined
          ? { ...invoiceData, category_metadata: categoryMetadata }
          : invoiceData;
      const result = await this.makeRequest('createDraftInvoice', {
        ...rest,
        invoiceData: mergedInvoiceData,
      });
      console.log("InvoiceApiClient: Draft created:", result.invoiceId);
      return result;
    } catch (error) {
      console.error("Failed to create draft invoice:", error);
      throw error;
    }
  }

  /**
   * Finalize a draft invoice with user edits
   */
  static async finalizeDraftInvoice(
    invoiceId: string,
    updates: Record<string, any>
  ): Promise<{ success: boolean; invoice?: Invoice; error?: string; alreadyFinalized?: boolean }> {
    try {
      console.log("InvoiceApiClient: Finalizing draft invoice:", invoiceId);
      const result = await this.makeRequest('finalizeDraftInvoice', { invoiceId, updates });

      console.log("InvoiceApiClient: Finalize result:", result.success);
      return result;
    } catch (error) {
      console.error("Failed to finalize draft invoice:", error);
      return { success: false, error: userFacingErrorMessage(error, translateOutsideReact('consultantErrors.fallback.finalizeDraftInvoice')) };
    }
  }

  /**
   * Update a draft invoice with extraction results
   */
  static async updateDraftExtraction(params: {
    invoiceId: string;
    extractedData: Record<string, any>;
    extractionMetadata: any;
    vendorAccountFields?: {
      vendor_iban?: string | null;
      vendor_bankgiro?: string | null;
      vendor_plusgiro?: string | null;
      vendor_account_number?: string | null;
      vendor_clearing_number?: string | null;
    } | null;
  }): Promise<{ success: boolean }> {
    try {
      console.log("InvoiceApiClient: Updating draft extraction:", params.invoiceId);
      const result = await this.makeRequest('updateDraftExtraction', params);
      console.log("InvoiceApiClient: Update result:", result.success);
      return result;
    } catch (error) {
      console.error("Failed to update draft extraction:", error);
      return { success: false };
    }
  }

  /**
   * Get all draft invoices
   */
  static async getDraftInvoices(): Promise<{ invoices: Invoice[] }> {
    try {
      console.log("InvoiceApiClient: Fetching draft invoices");
      const result = await this.makeRequest('getDraftInvoices');
      console.log("InvoiceApiClient: Received draft invoices:", result.invoices.length);
      return result;
    } catch (error) {
      console.error("Failed to fetch draft invoices:", error);
      throw error;
    }
  }

  /**
   * Get draft invoices filtered by extraction status
   */
  static async getDraftsByStatus(extractionStatus?: string): Promise<{ invoices: Invoice[] }> {
    try {
      console.log("InvoiceApiClient: Fetching drafts by status:", extractionStatus);
      const result = await this.makeRequest('getDraftsByStatus', { extractionStatus });
      console.log("InvoiceApiClient: Received drafts:", result.invoices.length);
      return result;
    } catch (error) {
      console.error("Failed to fetch drafts by status:", error);
      throw error;
    }
  }

  /**
   * Batch-create draft invoices for multiple uploaded files
   */
  static async getRecentlyFinalized(limit: number = 50): Promise<{ invoices: Invoice[] }> {
    return this.makeRequest('getRecentlyFinalized', { limit });
  }

  /**
   * FIN-1637: re-attempt finalization for every Needs Input draft whose auto-finalization
   * previously failed (e.g. a missing fiscal year), without re-entering any field data.
   */
  static async retryFailedFinalizations(limit: number = 50): Promise<{
    attempted: number;
    succeeded: number;
    stillBlocked: Array<{ id: string; vendor: string | null; reason: string }>;
  }> {
    try {
      console.log("InvoiceApiClient: Retrying failed finalizations");
      const result = await this.makeRequest('retryFailedFinalizations', { limit });
      console.log("InvoiceApiClient: Retry result:", result.succeeded, "/", result.attempted);
      return result;
    } catch (error) {
      console.error("Failed to retry finalizations:", error);
      throw error;
    }
  }

  static async createBatchDrafts(drafts: Array<{
    filePath: string;
    fileName: string;
    documentType: string;
  }>, companyId: string): Promise<{ results: Array<{ draftId: string; fileName: string } | { error: string; fileName: string }> }> {
    try {
      console.log("InvoiceApiClient: Creating batch drafts:", drafts.length);
      const result = await this.makeRequest('createBatchDrafts', { drafts, companyId });
      console.log("InvoiceApiClient: Batch drafts result:", result.results.length);
      return result;
    } catch (error) {
      console.error("Failed to create batch drafts:", error);
      throw error;
    }
  }

  static async deleteDocument(documentId: string): Promise<{ success: boolean }> {
    return this.makeRequest('deleteDocument', { documentId });
  }

  static async getRejectedDocuments(companyId: string): Promise<{ invoices: Invoice[] }> {
    return this.makeRequest('getRejectedDocuments', { companyId });
  }

  static async markRejectedSeen(ids: string[]): Promise<void> {
    await this.makeRequest('markRejectedSeen', { ids });
  }

  static async getUnseenRejectedCount(companyId: string): Promise<{ count: number }> {
    return this.makeRequest('getUnseenRejectedCount', { companyId });
  }

  static async getRejectedDocumentCompare(
    rejectedId: string,
    companyId: string
  ): Promise<{ rejected: Invoice; original: Invoice | null }> {
    return this.makeRequest('getRejectedDocumentCompare', { rejectedId, companyId });
  }
}
